import { Tooltip, makeStyles } from '@rbx/ui';
import React, { LegacyRef, useCallback, useMemo } from 'react';
import ClickAwayListener, { ClickAwayEvent } from '@modules/ui/shared/components/ClickAwayListener';
import { useTranslation } from '@rbx/intl';
import useAccessibilityStyles from '../styles/accessibilityStyles';
import useDeviceInfo from '../hooks/useDeviceInfo';

const useResponsiveTooltipStyles = makeStyles()((theme) => ({
  engineRevampTooltip: {
    backgroundColor: `${theme.palette.surface[400]} !important`,
    color: `${theme.palette.content.standard} !important`,
  },
  engineRevampArrow: {
    color: `${theme.palette.surface[400]} !important`,
  },
}));

type MobileTooltipChildrenProps = {
  // on mobile devices
  isOpen: boolean;
  onToggleOpen: () => void;
  children: React.ReactElement;
};

export const MobileTooltipChildren = React.forwardRef(function MobileChildren(
  { isOpen, onToggleOpen, children }: MobileTooltipChildrenProps,
  ref: LegacyRef<HTMLButtonElement>,
) {
  const { translate } = useTranslation();
  const {
    classes: { noStylesBtn },
  } = useAccessibilityStyles();
  return (
    // note: use button for accessibility
    <button
      ref={ref}
      className={noStylesBtn}
      type='button'
      onClick={() => onToggleOpen()}
      aria-label={translate('Label.OpenTooltip')}
      aria-expanded={isOpen}
      aria-haspopup='true'>
      {children}
    </button>
  );
});

type ResponsiveTooltipProps = React.ComponentProps<typeof Tooltip> & {
  clickableTooltipContentDataTooltipId?: string;
  // [data-tooltip-id] attr value on clickable element in tooltip [title]
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  initialIsOpen?: boolean;
  children: React.ReactElement;
  engineRevampEnabled?: boolean;
};

/**
 *
 * @param tooltipProps
 * @param tooltipProps
 * @returns Tooltip component with responsive design:
 *  on mobile device (Android/iOS), gets triggered by a button click
 *  on browsers, gets triggered by a hover event
 */
const ResponsiveTooltip = ({
  isOpen,
  setIsOpen,
  children,
  clickableTooltipContentDataTooltipId,
  engineRevampEnabled = false,
  ...tooltipProps
}: ResponsiveTooltipProps) => {
  const { isMobileDevice } = useDeviceInfo();
  const { classes: tooltipStyleClasses } = useResponsiveTooltipStyles();

  const onClickAway = useCallback(
    (e: ClickAwayEvent) => {
      if (clickableTooltipContentDataTooltipId) {
        let currTarget = e.target as Element;
        while (isMobileDevice && currTarget && currTarget.nodeType !== window.Node.DOCUMENT_NODE) {
          if (currTarget.getAttribute('data-tooltip-id') === clickableTooltipContentDataTooltipId) {
            // is clicking on element in tooltip => do not close tooltip, so
            // its click handler can get triggered, given that ClickAway
            // will suppress its click event
            return;
          }
          currTarget = currTarget.parentNode as Element;
        }
        setIsOpen(false);
      }
      setIsOpen(false);
    },
    [setIsOpen, clickableTooltipContentDataTooltipId, isMobileDevice],
  );

  const mergedTooltipProps = useMemo(() => {
    const baseProps = tooltipProps as React.ComponentProps<typeof Tooltip>;

    if (engineRevampEnabled) {
      return {
        ...baseProps,
        classes: {
          ...baseProps.classes,
          tooltip: tooltipStyleClasses.engineRevampTooltip,
          arrow: tooltipStyleClasses.engineRevampArrow,
        },
      };
    }

    return baseProps;
  }, [engineRevampEnabled, tooltipProps, tooltipStyleClasses]);

  return isMobileDevice ? (
    <ClickAwayListener
      // see: https://mui.com/material-ui/react-tooltip/#triggers on tooltip click
      // on mobile,opening tooltip on trigger button click, closing it on clickaway
      onClickAway={onClickAway}>
      <Tooltip {...mergedTooltipProps} open={isOpen}>
        <MobileTooltipChildren isOpen={isOpen} onToggleOpen={() => setIsOpen(!isOpen)}>
          {children}
        </MobileTooltipChildren>
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <Tooltip {...mergedTooltipProps}>{children}</Tooltip>
  );
};

export default ResponsiveTooltip;
